"use client";

import { useCallback } from "react";
import { useRouter } from "next/navigation";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Effect } from "effect";
import { RpcClient } from "@effect/rpc";
import { toast } from "sonner";
import { wireErrorDisplay } from "@/shared/error-diagnostics/wire";
import type { FeedPostId } from "@/contexts/feed/domain/feed-enums";
import type { SpotlightQueue, SpotlightScope, SpotlightView } from "@/contexts/feed/domain/spotlight/spotlight-settings";
import { feedSpotlightEq, FeedSpotlightRpc } from "./api/feed-spotlight-client";
import { spotlightKeys } from "./keys";
import { openAgentPrompt } from "../lib/open-agent-prompt";
import { scrollToPostWhenReady } from "../lib/scroll-to-post";
type SeenOutcome = "dismissed" | "opened" | "upgraded";

/** Where a landing carries the post, so the destination page shows its coach mark. */
export const withSpotlightParam = (path: string, postId: string): string => `${path}${path.includes("?") ? "&" : "?"}spotlight=${encodeURIComponent(postId)}`;

/**
 * What a broker can do with a spotlight (spec cova-feed/2026-10-09-001 §6.3).
 *
 * A popup must never trap anyone. A DISMISSAL closes at once and is recorded
 * best-effort: if the record fails, the worst case is that the spotlight comes
 * back next sitting. The BUTTON records first and moves second, and a failed
 * record keeps the popup open with the wire error's safe message, except
 * FEED_SPOTLIGHT_NOT_FOUND (the post was unpublished, disabled or re-shown
 * since it loaded), which closes it: there is nothing left to act on. Either
 * way the head leaves the cached queue, so the next one (if the sitting has
 * room) follows.
 */
export function useSpotlightActions(surface: SpotlightScope) {
  const router = useRouter();
  const qc = useQueryClient();
  const removeFromQueue = useCallback((postId: string) => qc.setQueryData<SpotlightQueue>(spotlightKeys.queue(surface), old => old ? {
    items: old.items.filter(item => item.postId !== postId)
  } : old), [qc, surface]);

  /** NOT_FOUND closes the popup; anything else keeps it open and says why. */
  const failedAct = (view: SpotlightView, then: () => void) => (error: unknown) => {
    const display = wireErrorDisplay(error);
    if (display.errorCode === "FEED_SPOTLIGHT_NOT_FOUND") {
      removeFromQueue(view.postId);
      then();
      return;
    }
    toast.error(display.message);
  };
  const markSeen = useMutation({
    ...feedSpotlightEq.mutationOptions({
      mutationKey: ["feed-spotlight", "mark-seen"],
      mutationFn: (v: {
        postId: string;
        version: number;
        outcome: SeenOutcome;
      }) => Effect.gen(function* () {
        const rpc = yield* RpcClient.make(FeedSpotlightRpc);
        return yield* rpc.markSpotlightSeen({
          postId: v.postId as FeedPostId,
          version: v.version,
          outcome: v.outcome
        });
      }).pipe(Effect.scoped)
    })
  });
  const requestAccess = useMutation({
    ...feedSpotlightEq.mutationOptions({
      mutationKey: ["feed-spotlight", "request-access"],
      mutationFn: (v_0: {
        postId: string;
        version: number;
      }) => Effect.gen(function* () {
        const rpc_0 = yield* RpcClient.make(FeedSpotlightRpc);
        return yield* rpc_0.requestFeatureAccess({
          postId: v_0.postId as FeedPostId,
          version: v_0.version
        });
      }).pipe(Effect.scoped)
    })
  });

  /** Records, then closes and moves on. NOT_FOUND closes without moving. */
  const record = (view_0: SpotlightView, outcome: SeenOutcome, close: () => void, move: () => void) => markSeen.mutate({
    postId: view_0.postId,
    version: view_0.version,
    outcome
  }, {
    onSuccess: () => {
      removeFromQueue(view_0.postId);
      close();
      move();
    },
    onError: failedAct(view_0, close)
  });

  /** Closes now; the record follows best-effort and its failure is silent. */
  const dismiss = (view_1: SpotlightView, then_0: () => void = () => undefined) => {
    removeFromQueue(view_1.postId);
    then_0();
    markSeen.mutate({
      postId: view_1.postId,
      version: view_1.version,
      outcome: "dismissed"
    });
  };

  /** The button. Returns nothing; the outcome is the navigation (or the toast). */
  const act = (view_2: SpotlightView, then_1: () => void = () => undefined) => {
    const cta = view_2.cta;
    switch (cta.kind) {
      case "open":
        return record(view_2, "opened", then_1, () => {
          const action = cta.action;
          switch (action.kind) {
            case "route":
              if (cta.path !== null) router.push(withSpotlightParam(cta.path, view_2.postId));
              return;
            case "agent":
              void openAgentPrompt(action.prompt, router.push);
              return;
            case "external":
              window.open(action.url, "_blank", "noopener");
              return;
            case "post":
              router.push(`/feed?post=${encodeURIComponent(action.postId)}`);
              scrollToPostWhenReady(action.postId);
              return;
          }
        });
      case "upgrade":
        return record(view_2, "upgraded", then_1, () => router.push(cta.href));
      case "request":
        return requestAccess.mutate({
          postId: view_2.postId,
          version: view_2.version
        }, {
          onSuccess: ({
            delivered
          }) => {
            removeFromQueue(view_2.postId);
            then_1();
            if (delivered) toast.success("Asked your firm admin. You'll hear back in Cova.");else toast("Your firm has no admin in Cova yet.");
          },
          onError: failedAct(view_2, then_1)
        });
    }
  };

  // A dismissal in flight never disables the next popup's buttons.
  const recordingAct = markSeen.isPending && markSeen.variables?.outcome !== "dismissed";
  return {
    dismiss,
    act,
    busy: recordingAct || requestAccess.isPending
  };
}